import { useEffect, useRef, useState } from 'react';
import { BadgeCheck, CircleDashed, ReceiptText, Timer } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import type { TimeEntry } from '@stfors/shared-types';
import {
  useTimeEntries,
  useServiceRates,
  useDeleteTimeEntry,
  useApproveTimeEntry,
  useCreateTimeEntry,
  effectiveRates,
} from '../api/queries/time-entries';
import { useProjects } from '../api/queries/projects';
import { useUnbilledWipReport } from '../api/queries/reports';
import { ApiError, api } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { can } from '../auth/store';
import { fmtDate, dateTimeParts, rangeEndParts } from '../utils/date';
import { endFromDuration, roundToTenthHours } from '../utils/duration';
import { Modal } from '../components/Modal';
import { EditTimeEntryModal } from '../components/EditTimeEntryModal';
import { ProjectOptions } from '../components/ProjectOptions';
import { TimeRangeFields, type TimeRangeValue } from '../components/TimeRangeFields';
import { MoneyInput } from '../components/MoneyInput';
import { SoldAssetPicker } from '../components/SoldAssetPicker';
import { MatterSelect } from '../components/MatterSelect';
import { SaleDateField, startOfLocalDay } from '../components/SaleDateField';
import { entryAmount, fmtEntryHours, isFlatRate } from '../utils/time-entry';
import { BulkActionBar } from '../components/BulkActionBar';
import { IconAction } from '../components/IconAction';
import { SortableTh } from '../components/SortableTh';
import { ColumnSettings } from '../components/ColumnSettings';
import { useColVisibility, type ColumnDef } from '../hooks/useColVisibility';
import { TableScroll } from '../components/TableScroll';
import { useClientSort } from '../hooks/useClientSort';
import { useRowSelection } from '../hooks/useRowSelection';
import './TimeEntriesPage.css';

// Order here is the order in the menu, not in the table. `status` is the pinned
// actions cell — always present, so it isn't offered.
const TIME_COLUMNS: readonly ColumnDef[] = [
  // Just Start / End: the date-over-time cells below say what they hold.
  { key: 'date', label: 'Start' },
  { key: 'end', label: 'End' },
  { key: 'project', label: 'Project' },
  { key: 'who', label: 'Who' },
  { key: 'service', label: 'Service' },
  { key: 'matter', label: 'Matter' },
  { key: 'hours', label: 'Hours' },
  { key: 'rate', label: 'Rate' },
  { key: 'subtotal', label: 'Subtotal' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status', locked: true },
];

// Most of the time you're looking at your own entries, so Who is noise on a
// table that already scrolls sideways. It's one click away in the menu.
const TIME_DEFAULT_HIDDEN = ['who'];

type TimeSortKey =
  | 'date'
  | 'end'
  | 'project'
  | 'who'
  | 'service'
  | 'matter'
  | 'hours'
  | 'rate'
  | 'subtotal'
  | 'description'
  | 'status';

/**
 * A fresh quick-log triad. Built on demand (not a module constant) so each
 * open — and each post-submit reset — starts from the current clock.
 */
function blankQuickRange(): TimeRangeValue {
  // End and duration start EMPTY — only the start is worth guessing, and today
  // is nearly always right. A seeded "0.10" duration and a six-minute end were
  // placeholders that had to be cleared before they could be replaced.
  //
  // Both, not just the duration: a stale end with no duration is a dead end.
  // Move the start past that end and the conflict dialog has nothing to offer
  // as its "move the end" answer.
  //
  // Built on demand, not a module constant, so each open starts from the
  // current clock. Every other time-entry form seeds this way too.
  return { start: Date.now(), end: null, hours: '' };
}

/**
 * Row status as a single glyph, so the three-state pill can live in the pinned
 * actions cell instead of owning a ~130px column of its own.
 *
 * Not a button — nothing here is clickable, and it must not read as a disabled
 * one. The tooltip carries the detail the pill used to spell out, notably WHICH
 * invoice a billed entry landed on. Deliberately none of the glyphs the approve/
 * unapprove button uses (CheckCircle2 / CircleSlash), which sit right beside it.
 */
function StatusIcon({ entry }: { entry: TimeEntry }): JSX.Element {
  const state =
    entry.invoice_id !== null
      ? { Icon: ReceiptText, tone: 'te-status-billed', label: `Billed on invoice #${entry.invoice_id}` }
      : entry.approved_at
        ? {
            Icon: BadgeCheck,
            tone: 'te-status-approved',
            label: `Approved ${fmtDate(entry.approved_at)} — ready to bill`,
          }
        : { Icon: CircleDashed, tone: 'te-status-unbilled', label: 'Unbilled — not yet approved' };
  const { Icon } = state;

  return (
    <span
      className={`te-status-icon ${state.tone}`}
      role="img"
      aria-label={state.label}
      title={state.label}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden />
    </span>
  );
}

/**
 * A datetime as two stacked lines — date over time.
 *
 * Side by side, "05/29/2026 2:30 PM" is the widest thing in the table, and
 * there are two of these columns. Stacking costs a line of height the wrapping
 * Description column was already spending and halves what the pair costs
 * sideways, on a table that scrolls horizontally.
 */
function DateTimeCell({ date, time }: { date: string | null; time: string | null }): JSX.Element {
  return (
    <span className="te-dt">
      {date && <span className="te-dt-date">{date}</span>}
      {time && <span className="te-dt-time">{time}</span>}
    </span>
  );
}

interface TimerState {
  project_id: number;
  service_rate_id: number;
  service_rate_name: string;
  started_at: number;
  description: string;
  /** The project's matter (0061). Absent on timers started before it existed. */
  matter_id?: number | null;
}
const TIMER_KEY = 'stfors.timer.v1';
function readTimer(): TimerState | null {
  try {
    const raw = localStorage.getItem(TIMER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TimerState;
    if (typeof parsed.project_id !== 'number') return null;
    if (typeof parsed.started_at !== 'number') return null;
    // Older timers (pre rate-picker) lack a rate — discard them gracefully.
    if (typeof parsed.service_rate_id !== 'number' || parsed.service_rate_id <= 0) return null;
    return parsed;
  } catch {
    return null;
  }
}
function writeTimer(t: TimerState | null): void {
  if (t === null) localStorage.removeItem(TIMER_KEY);
  else localStorage.setItem(TIMER_KEY, JSON.stringify(t));
}
function fmtElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

import { formatMoney } from '../utils/money';

export function TimeEntriesPage(): JSX.Element {
  const qc = useQueryClient();
  const [projectFilter, setProjectFilter] = useState<number | undefined>(undefined);
  const { data: projectsData } = useProjects();
  const { data, isPending, error } = useTimeEntries(projectFilter);
  // Server-side, so it covers ALL matching entries — the row list below is
  // capped at 200 — and prices off the project's effective rate sheet, which
  // the per-row service_rate_amount does not.
  const wipQuery = useUnbilledWipReport(projectFilter ?? null);
  const deleteTime = useDeleteTimeEntry();
  const approveTime = useApproveTimeEntry();
  const rowSel = useRowSelection();
  const [bulkBusy, setBulkBusy] = useState(false);

  const clearRowSel = rowSel.clear;
  useEffect(() => {
    clearRowSel();
  }, [projectFilter, clearRowSel]);

  const createTime = useCreateTimeEntry();
  const [timer, setTimer] = useState<TimerState | null>(() => readTimer());
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!timer) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [timer]);
  const [timerRateId, setTimerRateId] = useState<number>(0);
  const [timerDesc, setTimerDesc] = useState('');
  const [timerMatterId, setTimerMatterId] = useState<number | null>(null);
  // A matter belongs to one project — a new filter starts with none.
  useEffect(() => { setTimerMatterId(null); }, [projectFilter]);
  const [timerError, setTimerError] = useState<string | null>(null);
  // Collapsed by default — most time is recorded after the fact via Quick add,
  // so the timer shouldn't cost a row of the header to everyone who isn't
  // using it. Kept out of localStorage on purpose: the point is that it starts
  // out of the way, and a timer left running is what re-opens it.
  const [timerOpen, setTimerOpen] = useState(false);

  // The timer tracks against the project chosen in the header filter — no
  // separate picker. Rates are collapsed to the effective rate per name so
  // the picker offers exactly what would bill (project tier included).
  const ratesQuery = useServiceRates(projectFilter);
  const timerRates = effectiveRates(ratesQuery.data?.service_rates ?? []);

  // Default the rate once a project is chosen, so the timer is one-click ready.
  useEffect(() => {
    if (!timer && projectFilter && timerRateId === 0) {
      const eff = effectiveRates(ratesQuery.data?.service_rates ?? []);
      if (eff.length) setTimerRateId(eff[0]!.id);
    }
  }, [ratesQuery.data, projectFilter, timerRateId, timer]);

  // Quick-add manual entry — its own state so it never touches the timer.
  const [quickOpen, setQuickOpen] = useState(false);
  const [qProjectId, setQProjectId] = useState(0);
  const [qRateId, setQRateId] = useState(0);
  const [qRange, setQRange] = useState<TimeRangeValue>(blankQuickRange);
  const [qDesc, setQDesc] = useState('');
  const [qTopError, setQTopError] = useState<string | null>(null);
  const [qFieldErrors, setQFieldErrors] = useState<Record<string, string>>({});
  const qRatesQuery = useServiceRates(qProjectId || undefined);
  const qRates = effectiveRates(qRatesQuery.data?.service_rates ?? []);
  const qRate = qRates.find((r) => r.id === qRateId);
  const qFlat = isFlatRate(qRate);
  // A flat-fee rate's amount is only the default: each entry can bill its own.
  // Re-seeded whenever the rate changes, so switching rates never leaves the
  // previous service's fee behind.
  const [qFee, setQFee] = useState('');
  // The drive this charge sells, if any (0058) — flat-fee rates only.
  const [qAssetId, setQAssetId] = useState<number | null>(null);
  // The project's matter this work belongs to (0061).
  const [qMatterId, setQMatterId] = useState<number | null>(null);
  // A product-sale rate (0059) makes the entry a SALE: a sale date, no time or
  // hours, and the device it sold.
  const qSale = qRate?.is_product_sale === true;
  const qRateAmount = qRate?.amount;

  // ---- Duplicate and edit ----
  // The entry the open modal was copied from (title + note), and what the copy
  // still has to apply once the project's rates load: its own fee — which the
  // fee effect below would otherwise replace with the rate's default — and its
  // rate's NAME, for when that rate is shadowed on the effective sheet (a
  // Global the project later overrode) and its id isn't in the picker.
  const [qDupFrom, setQDupFrom] = useState<TimeEntry | null>(null);
  const qDupPending = useRef<{ rateName: string | null; fee: string | null } | null>(null);

  useEffect(() => {
    const pending = qDupPending.current;
    if (pending && pending.fee !== null && qFlat) {
      setQFee(pending.fee);
      pending.fee = null;
      return;
    }
    setQFee(qFlat && qRateAmount !== undefined ? qRateAmount : '');
  }, [qRateId, qFlat, qRateAmount]);

  useEffect(() => {
    const pending = qDupPending.current;
    if (!pending || qRatesQuery.isPending || qRateId === 0 || qRate) return;
    const same = effectiveRates(qRatesQuery.data?.service_rates ?? [])
      .find((r) => r.name === pending.rateName);
    setQRateId(same?.id ?? 0);
  }, [qRatesQuery.isPending, qRatesQuery.data, qRateId, qRate]);

  /** Blank the Log Time modal, so a cancelled copy never leaks into the next one. */
  const resetQuick = (projectId: number): void => {
    qDupPending.current = null;
    setQDupFrom(null);
    setQProjectId(projectId);
    setQRateId(0);
    setQRange(blankQuickRange());
    setQDesc('');
    setQAssetId(null);
    setQMatterId(null);
    setQTopError(null);
    setQFieldErrors({});
  };

  /**
   * Open Log Time pre-filled from `e`; nothing is saved until the user saves.
   * Not copied: the date and time (now — it's new work), the device sold (a
   * drive is sold once), and approval / invoice (a new entry starts unbilled).
   */
  const startDuplicate = (e: TimeEntry): void => {
    resetQuick(e.project_id);
    qDupPending.current = { rateName: e.service_rate_name ?? null, fee: e.flat_amount };
    setQDupFrom(e);
    setQRateId(e.service_rate_id);
    // The matter is the kind of work, so a copy keeps it.
    setQMatterId(e.matter_id ?? null);
    setQRange({ start: Date.now(), end: null, hours: e.hours ?? '' });
    setQDesc(e.description);
    setQuickOpen(true);
  };

  // Auto-default the modal's rate when a project is picked (same as the timer).
  useEffect(() => {
    if (qProjectId > 0 && qRateId === 0) {
      const eff = effectiveRates(qRatesQuery.data?.service_rates ?? []);
      if (eff.length) setQRateId(eff[0]!.id);
    }
  }, [qRatesQuery.data, qProjectId, qRateId]);

  const submitQuick = async (): Promise<void> => {
    setQTopError(null);
    setQFieldErrors({});
    if (qProjectId <= 0) return setQFieldErrors({ project_id: 'Select a project' });
    if (qRateId <= 0) return setQFieldErrors({ service_rate_id: 'Select a rate' });
    if (qRange.start === null) return setQFieldErrors({ date: 'Pick a date' });
    if (!qSale && qRange.end !== null && qRange.end <= qRange.start) {
      return setQFieldErrors({ ended_at_epoch: 'End time must be after the start time' });
    }
    // Never checked before, because the seed was always '0.10'. Now that the
    // field opens blank, an unfilled duration would post '' and come back as a
    // 400 from the API's `^\d+(\.\d{1,2})?$`. A flat fee bills once, so its
    // duration may stay blank — but one that IS entered must still be valid.
    const hoursBlank = qSale || qRange.hours.trim() === '';
    if (!(qFlat && hoursBlank)
        && (!/^\d+(\.\d{1,2})?$/.test(qRange.hours.trim()) || Number(qRange.hours) <= 0)) {
      return setQFieldErrors({ hours: 'Enter a duration, or an end time' });
    }
    if (qFlat && !/^\d+(\.\d{1,2})?$/.test(qFee.trim())) {
      return setQFieldErrors({ flat_amount: 'Enter the fee for this entry' });
    }
    if (qDesc.trim().length === 0) return setQFieldErrors({ description: 'Add a description' });

    try {
      await createTime.mutateAsync({
        project_id: qProjectId,
        service_rate_id: qRateId,
        hours: hoursBlank ? null : qRange.hours,
        ...(qFlat ? { flat_amount: qFee.trim() } : {}),
        ...(qSale ? { asset_id: qAssetId } : {}),
        matter_id: qMatterId,
        // A sale is dated, not timed — no span, and the date at local midnight.
        date: qSale ? startOfLocalDay(qRange.start) : qRange.start,
        description: qDesc.trim(),
        // Both-or-neither: only record the span when an end was entered.
        ...(!qSale && qRange.end !== null
          ? { started_at_epoch: qRange.start, ended_at_epoch: qRange.end }
          : {}),
      });
      // Reset and close on success (the list re-fetches via invalidation).
      setQuickOpen(false);
      resetQuick(0);
      setQAssetId(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as
          | { details?: { fieldErrors?: Record<string, string[]> } }
          | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setQFieldErrors(flat);
          setQTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setQTopError("You don't have permission to log time on this project.");
        return;
      }
      // The API's own reason — e.g. "That asset is already sold on another time entry".
      setQTopError(apiErrorText(err, 'Could not log the time entry.'));
    }
  };

  // Editing happens in a modal (<EditTimeEntryModal>) rather than inline, so
  // every field is reachable — the row only holds the trigger.
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const onDelete = (id: number, billed: boolean) => {
    if (billed) {
      setActionError('Cannot delete a billed entry.');
      return;
    }
    if (!confirm('Delete this time entry?')) return;
    setActionError(null);
    deleteTime.mutate(id, {
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409) {
          setActionError('Cannot delete a billed entry.');
        } else if (err instanceof ApiError && err.status === 403) {
          setActionError("You can't delete time entries.");
        } else {
          setActionError('Delete failed.');
        }
      },
    });
  };

  const entries = data?.time_entries ?? [];
  const projects = projectsData?.projects ?? [];
  // A running timer pins the row open — hiding one would leave time running
  // where nobody can see it.
  const showTimer = timerOpen || timer !== null;
  const timerToggleLabel = timer !== null
    ? 'Timer running — show the controls'
    : !projectFilter
      ? 'Select a project first, then start a timer'
      : showTimer ? 'Hide the timer' : 'Start a timer';

  const vis = useColVisibility('time-entries', TIME_COLUMNS, TIME_DEFAULT_HIDDEN);
  const sort = useClientSort(entries, (e, key: TimeSortKey) => {
    switch (key) {
      // `date` IS the start moment (the timer writes it that way); the
      // separate started_at_epoch only exists on rows that recorded a real
      // span, and it agrees with `date` when it does.
      case 'date': return e.started_at_epoch ?? e.date;
      // Null for a row with no recorded end — compareSortValues groups those
      // at one end rather than treating a missing span as epoch 0.
      case 'end': return e.ended_at_epoch ?? null;
      case 'project': return e.project_case_number ?? `#${e.project_id}`;
      case 'who': return e.user_display_name ?? `User #${e.user_id}`;
      case 'service': return e.service_rate_name ?? `Rate #${e.service_rate_id}`;
      case 'matter': return e.matter_name ?? null;
      case 'hours': return e.hours === null ? null : Number(e.hours);
      case 'rate': return Number(e.flat_amount ?? e.service_rate_amount ?? 0);
      case 'subtotal': return entryAmount(e);
      case 'description': return e.description;
      case 'status':
        if (e.invoice_id !== null) return 2;
        if (e.approved_at) return 1;
        return 0;
      default: return '';
    }
  }, { key: 'date', dir: 'desc' });

  const approvableIds = rowSel.selectedIds.filter((id) => {
    const e = entries.find((x) => x.id === id);
    return e != null && e.approved_at == null && e.invoice_id == null;
  });

  const bulkApproveTime = async () => {
    if (approvableIds.length === 0 || !can('time_entry.approve')) return;
    setActionError(null);
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        approvableIds.map((id) => api.post(`/api/time-entries/${id}/approve`, {})),
      );
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed > 0) {
        setActionError(
          failed === results.length
            ? 'Could not approve the selected time entries.'
            : `Approved ${results.length - failed}; ${failed} failed.`,
        );
      }
      rowSel.clear();
    } finally {
      void qc.invalidateQueries({ queryKey: ['time-entries'] });
      setBulkBusy(false);
    }
  };

  if (isPending) return <p className="te-status">Loading time entries…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? "You don't have permission to view time entries."
        : 'Failed to load time entries.';
    return <p className="te-status te-error">{msg}</p>;
  }

  // Summary: total hours + estimated dollars (across all visible rows)
  const totalHours = entries.reduce((acc, e) => acc + Number(e.hours ?? 0), 0);
  const totalDollars = entries.reduce((acc, e) => acc + entryAmount(e), 0);
  // Unbilled comes from the server (see wipQuery above), not from `entries`:
  // a client-side sum would be truncated by the 200-row cap and priced off the
  // rate each entry points at rather than the project's effective sheet.
  const wipTotals = wipQuery.data?.totals;

  return (
    <section className="page-stack page-stack-wide">
      {/* One bar: title + totals + controls on the first line, the timer on the
          second. These were three stacked cards costing ~350px of height above
          a table that already has to scroll — the totals are reference, not
          the point of the page, so they read as inline stats rather than
          hero numbers. */}
      <div className="card card-tight te-bar">
        <div className="te-bar-main">
          <h2>Time Entries</h2>

          <dl className="te-kpis">
            <div className="te-kpi">
              <dt>Total</dt>
              <dd>{totalHours.toFixed(2)}</dd>
            </div>
            <div
              className="te-kpi"
              title="All unbilled time, not just the rows shown. Work on an unsent draft invoice still counts."
            >
              <dt>Unbilled</dt>
              <dd className="te-kpi-warn">
                {wipTotals
                  ? `${Number(wipTotals.unbilled_hours).toFixed(2)} hr · ${formatMoney(wipTotals.unbilled_time_value)}`
                  : '—'}
              </dd>
            </div>
            <div className="te-kpi">
              <dt>Est. value</dt>
              <dd>{formatMoney(String(totalDollars.toFixed(2)))}</dd>
            </div>
          </dl>

          <div className="te-bar-controls">
            <div className="te-project-pick">
              <label className="te-project-hint" htmlFor="te-project-filter">
                Select a Project Below to Start Timer
              </label>
              <select
                id="te-project-filter"
                className="form-input te-filter"
                aria-label="Filter by project"
                value={projectFilter ?? 0}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setProjectFilter(v > 0 ? v : undefined);
                  // Re-derive the timer's rate for the newly selected project.
                  setTimerRateId(0);
                }}
              >
                <option value={0}>All projects</option>
                <ProjectOptions projects={projects} />
              </select>
            </div>
            {can('time_entry.write') && (
              <>
                <button
                  type="button"
                  className="btn-cyan-primary"
                  onClick={() => {
                    // Prefill the project from the header selection, if any.
                    resetQuick(projectFilter ?? 0);
                    setQuickOpen(true);
                  }}
                >
                  + Quick add
                </button>
                {/* The timer is the rarer path — most time is recorded after
                    the fact through Quick add — so it's an icon that reveals
                    the controls rather than a row taking up space unasked. */}
                <button
                  type="button"
                  className={`te-timer-toggle${showTimer ? ' te-timer-toggle-on' : ''}`}
                  onClick={() => setTimerOpen((o) => !o)}
                  disabled={!projectFilter && timer === null}
                  aria-pressed={showTimer}
                  aria-label={timerToggleLabel}
                  title={timerToggleLabel}
                >
                  <Timer size={16} strokeWidth={1.75} aria-hidden />
                </button>
              </>
            )}
          </div>
        </div>

        {/* The timer, revealed on demand. A RUNNING timer forces it open —
            hiding one would lose track of time nobody could see. */}
        {can('time_entry.write') && showTimer && (
          <div className="te-timer-row">
            <Timer size={15} strokeWidth={1.75} aria-hidden className="te-timer-glyph" />
            {timer ? (
              <>
                <div className="te-timer-running">
                  <span className="te-timer-elapsed">{fmtElapsed(Date.now() - timer.started_at)}</span>
                  <span className="te-timer-meta">
                    {(() => {
                      const p = projects.find((pp) => pp.id === timer.project_id);
                      const proj = p ? `${p.case_number} — ${p.title}` : `Project #${timer.project_id}`;
                      return timer.service_rate_name ? `${proj} · ${timer.service_rate_name}` : proj;
                    })()}
                  </span>
                  {timer.description && <span className="te-timer-desc">"{timer.description}"</span>}
                </div>
                <div className="te-timer-actions">
                <button
                  type="button"
                  className="btn-charcoal-secondary"
                  onClick={() => {
                    if (!confirm('Discard the running timer?')) return;
                    setTimer(null);
                    writeTimer(null);
                  }}
                >
                  Discard
                </button>
                <button
                  type="button"
                  className="btn-cyan-primary"
                  disabled={createTime.isPending}
                  onClick={async () => {
                    setTimerError(null);
                    const ended = Date.now();
                    const elapsedHours = (ended - timer.started_at) / 3_600_000;
                    try {
                      // Use the rate chosen when the timer started. Snap the
                      // elapsed time to the nearest 6 min (floors at 0.1),
                      // matching manual entry; Discard handles accidental runs.
                      // The timer knows both ends of the span, so record them
                      // verbatim — billing rounds, the record shouldn't.
                      await createTime.mutateAsync({
                        project_id: timer.project_id,
                        service_rate_id: timer.service_rate_id,
                        hours: roundToTenthHours(elapsedHours),
                        date: timer.started_at,
                        description: timer.description || 'Time tracked via timer',
                        matter_id: timer.matter_id ?? null,
                        started_at_epoch: timer.started_at,
                        ended_at_epoch: ended,
                      });
                      setTimer(null);
                      writeTimer(null);
                    } catch (err) {
                      setTimerError(err instanceof ApiError ? `Save failed (${err.status}).` : 'Save failed.');
                    }
                  }}
                >
                  Stop &amp; save
                </button>
                </div>
              </>
            ) : !projectFilter ? (
              // Only reachable if the project filter is cleared while the row
              // is open — the toggle itself is disabled without a project.
              <span className="te-timer-hint">Pick a project to start the timer.</span>
            ) : (
              <>
              <select
                className="form-input te-timer-rate"
                value={timerRateId}
                onChange={(e) => setTimerRateId(Number(e.target.value))}
                disabled={ratesQuery.isPending}
              >
                <option value={0}>
                  {ratesQuery.isPending ? 'Loading rates…' : 'Select a rate…'}
                </option>
                {timerRates.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                    {r.project_override_for
                      ? ' (project rate)'
                      : r.client_override_for
                      ? ' (client rate)'
                      : ''}{' '}
                    — {formatMoney(r.amount)}/{r.rate_type === 'hourly' ? 'hr' : 'flat'}
                  </option>
                ))}
              </select>
              <MatterSelect
                compact
                className="te-timer-matter"
                projectId={projectFilter}
                value={timerMatterId}
                onChange={setTimerMatterId}
              />
              <input
                type="text"
                className="form-input te-timer-what"
                value={timerDesc}
                onChange={(e) => setTimerDesc(e.target.value)}
                placeholder="What are you working on?"
                maxLength={500}
              />
              <button
                type="button"
                className="btn-cyan-primary"
                disabled={timerRateId <= 0}
                onClick={() => {
                  if (!projectFilter) return;
                  setTimerError(null);
                  const rate = timerRates.find((r) => r.id === timerRateId);
                  const next: TimerState = {
                    project_id: projectFilter,
                    service_rate_id: timerRateId,
                    service_rate_name: rate?.name ?? '',
                    started_at: Date.now(),
                    description: timerDesc.trim(),
                    matter_id: timerMatterId,
                  };
                  setTimer(next);
                  writeTimer(next);
                  setTimerRateId(0);
                  setTimerDesc('');
                }}
              >
                ▶ Start
              </button>
              </>
            )}
          </div>
        )}

        {/* Hoisted out of the running branch: a failed Stop & save used to
            vanish along with the timer state that produced it. */}
        {timerError && <p className="te-error-banner" role="alert">{timerError}</p>}
      </div>

      {entries.length === 0 ? (
        <div className="card">
          <div className="te-empty">
            <p>No time entries {projectFilter ? 'for this project' : 'yet'}.</p>
            <p className="te-empty-meta">
              {can('time_entry.write')
                ? 'Use "+ Quick add" above to record your first entry.'
                : 'Time entries are created by examiners and contractors.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          <BulkActionBar
            count={rowSel.count}
            onClear={rowSel.clear}
            busy={bulkBusy}
            hint={
              can('time_entry.approve')
                ? (approvableIds.length > 0
                  ? `${approvableIds.length} ready to approve`
                  : 'None of the selected are pending approval')
                : undefined
            }
          >
            {can('time_entry.approve') && (
              <button
                type="button"
                className="btn-cyan-primary"
                disabled={bulkBusy || approvableIds.length === 0}
                onClick={() => void bulkApproveTime()}
              >
                {bulkBusy ? 'Approving…' : `Approve${approvableIds.length > 0 ? ` (${approvableIds.length})` : ''}`}
              </button>
            )}
          </BulkActionBar>
        <div className="card card-tight te-table-card table-scroll-card">
          {actionError && (
            <p className="te-error-banner" role="alert">{actionError}</p>
          )}
          <div className="te-table-bar">
            <p className="te-table-hint">
              Hold <kbd>Ctrl</kbd> (Mac: <kbd>⌘</kbd>) and click rows to multi-select for bulk approve.
            </p>
            <ColumnSettings vis={vis} />
          </div>
          <TableScroll id="time-entries">
          <table className="te-table">
            <thead>
              <tr>
                {vis.isVisible('date') && <SortableTh column="date" label="Start" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('end') && <SortableTh column="end" label="End" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('project') && <SortableTh column="project" label="Project" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('who') && <SortableTh column="who" label="Who" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('service') && <SortableTh column="service" label="Service" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('matter') && <SortableTh column="matter" label="Matter" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} />}
                {vis.isVisible('hours') && <SortableTh column="hours" label="Hours" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="te-numeric" />}
                {vis.isVisible('rate') && <SortableTh column="rate" label="Rate" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="te-numeric" />}
                {vis.isVisible('subtotal') && <SortableTh column="subtotal" label="Subtotal" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} numeric className="te-numeric" />}
                {/* Not resizable: Description takes whatever width the other columns
                    leave (see .te-table in the stylesheet), so a dragged width
                    would only push it under the pinned Status column again. */}
                {vis.isVisible('description') && <SortableTh column="description" label="Description" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} resizable={false} />}
                {/* Status folded into the pinned actions column as a leading
                    glyph — a whole column for a three-state pill cost ~130px.
                    The header keeps the "status" sort key, and costs no extra
                    width: the four icons below it are wider than the label. */}
                <SortableTh column="status" label="Status" sortKey={sort.sortKey} sortDir={sort.sortDir} onSort={sort.toggle} className="sticky-actions" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((e) => {
                const subtotal = entryAmount(e);
                const billed = e.invoice_id !== null;
                const selected = rowSel.isSelected(e.id);
                return (
                  <tr
                    key={e.id}
                    className={`${billed ? 'te-row-billed' : ''}${selected ? ' table-row-selected' : ''}`.trim()}
                    aria-selected={selected}
                    onMouseDown={rowSel.suppressTextSelect}
                    onClick={(ev) => {
                      // Only multi-select gestures change selection; plain click
                      // leaves row actions (edit/approve) alone.
                      if (ev.ctrlKey || ev.metaKey || ev.shiftKey) {
                        rowSel.handleRowClick(ev, e.id, sort.sorted.map((x) => x.id));
                      }
                    }}
                  >
                    {/* `date` is the start moment for anything the timer or the
                        start/end triad produced; started_at_epoch only exists
                        on rows that recorded a real span and agrees with it
                        there. Legacy manual rows are pinned to local midnight,
                        which dateTimeParts reports as a date with no time. */}
                    {vis.isVisible('date') && (
                      <td><DateTimeCell {...dateTimeParts(e.started_at_epoch ?? e.date)} /></td>
                    )}
                    {/* Most entries start and end on one day, so the end shows
                        the time alone — the start's date is right there. A date
                        that DOES appear means the entry ran past midnight. */}
                    {vis.isVisible('end') && (
                      <td>
                        {(() => {
                          const p = rangeEndParts(e.started_at_epoch ?? e.date, e.ended_at_epoch);
                          return p === null ? '—' : <DateTimeCell {...p} />;
                        })()}
                      </td>
                    )}
                    {vis.isVisible('project') && (
                      <td><code>{e.project_case_number ?? `#${e.project_id}`}</code></td>
                    )}
                    {vis.isVisible('who') && (
                      <td>{e.user_display_name ?? `User #${e.user_id}`}</td>
                    )}
                    {vis.isVisible('service') && (
                      <td>
                        <div className="te-service-text">
                          {e.service_rate_name ?? `Rate #${e.service_rate_id}`}
                          {/* The drive this charge sold (0058) — internal, not on the invoice. */}
                          {e.asset_name && <div className="te-sold-asset">Device: {e.asset_name}</div>}
                        </div>
                      </td>
                    )}
                    {vis.isVisible('matter') && (
                      <td>{e.matter_name ?? '—'}</td>
                    )}
                    {vis.isVisible('hours') && (
                      <td className="te-numeric">{fmtEntryHours(e.hours)}</td>
                    )}
                    {vis.isVisible('rate') && (
                      <td className="te-numeric">
                        {/* A flat-fee entry shows the fee it bills, not the rate's default. */}
                        {formatMoney(e.flat_amount ?? e.service_rate_amount ?? '0')}
                        {e.service_rate_type === 'flat_fee' && <span className="te-flat-tag"> flat</span>}
                      </td>
                    )}
                    {vis.isVisible('subtotal') && (
                      <td className="te-numeric te-subtotal">{formatMoney(String(subtotal.toFixed(2)))}</td>
                    )}
                    {vis.isVisible('description') && (
                      <td className="te-desc">
                        {/* The width cap has to live on a block INSIDE the cell —
                            see .te-desc-text in the stylesheet. */}
                        <div className="te-desc-text">{e.description}</div>
                      </td>
                    )}
                    <td
                      className="te-actions sticky-actions"
                      onClick={(ev) => ev.stopPropagation()}
                    >
                      <StatusIcon entry={e} />
                      {can('time_entry.approve') && !billed && (
                        <IconAction
                          action={e.approved_at ? 'unapprove' : 'approve'}
                          onClick={() => {
                            setActionError(null);
                            approveTime.mutate(
                              { id: e.id, approve: !e.approved_at },
                              {
                                onError: () => setActionError('Approval action failed.'),
                              },
                            );
                          }}
                          disabled={approveTime.isPending}
                          iconSize={16}
                        />
                      )}
                      {/* Billed entries too — copying one is how the next
                          visit gets logged. The copy starts unbilled. */}
                      {can('time_entry.write') && (
                        <IconAction
                          action="duplicate"
                          onClick={() => {
                            setActionError(null);
                            startDuplicate(e);
                          }}
                          iconSize={16}
                        />
                      )}
                      {!billed && can('time_entry.write') && (
                        <IconAction
                          action="edit"
                          onClick={() => {
                            setActionError(null);
                            setEditing(e);
                          }}
                          title="Edit this entry"
                          iconSize={16}
                        />
                      )}
                      {!billed && can('time_entry.delete') && (
                        <IconAction
                          action="delete"
                          onClick={() => onDelete(e.id, billed)}
                          disabled={deleteTime.isPending}
                          iconSize={16}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
              </TableScroll>
        </div>
        </>
      )}

      <Modal
        title={qDupFrom ? 'Duplicate Time Entry' : 'Log Time'}
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        busy={createTime.isPending}
        footer={
          <>
            <button
              type="button"
              className="btn-charcoal-secondary"
              onClick={() => setQuickOpen(false)}
              disabled={createTime.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={submitQuick}
              disabled={
                createTime.isPending ||
                qProjectId <= 0 ||
                qRateId <= 0 ||
                qRange.start === null ||
                (!qFlat && qRange.hours.trim().length === 0) ||
                (qFlat && qFee.trim().length === 0) ||
                qDesc.trim().length === 0
              }
            >
              {createTime.isPending ? 'Saving…' : 'Log time'}
            </button>
          </>
        }
      >
        <div className="te-quick-form">
          {qDupFrom && (
            <p className="te-dup-note" role="status">
              Copied from the {fmtDate(qDupFrom.date)} entry. The date and time are now
              {qDupFrom.asset_id != null ? ', and the device sold is not copied' : ''} — change
              anything, then log it as a new entry.
            </p>
          )}
          <label className="te-quick-field">
            <span>Project *</span>
            <select
              className="form-input"
              value={qProjectId}
              onChange={(e) => {
                setQProjectId(Number(e.target.value));
                setQRateId(0);
                setQMatterId(null);
              }}
            >
              <option value={0}>Select a project…</option>
              <ProjectOptions projects={projects} />
            </select>
            {qFieldErrors['project_id'] && (
              <span className="field-error">{qFieldErrors['project_id']}</span>
            )}
          </label>

          <MatterSelect
            className="te-quick-field"
            projectId={qProjectId || null}
            value={qMatterId}
            onChange={setQMatterId}
            error={qFieldErrors['matter_id']}
          />

          <label className="te-quick-field">
            <span>Service rate *</span>
            <select
              className="form-input"
              value={qRateId}
              onChange={(e) => setQRateId(Number(e.target.value))}
              disabled={qProjectId <= 0 || qRatesQuery.isPending}
            >
              <option value={0}>
                {qProjectId <= 0
                  ? 'Pick a project first'
                  : qRatesQuery.isPending
                  ? 'Loading rates…'
                  : 'Select a rate…'}
              </option>
              {qRates.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                  {r.project_override_for
                    ? ' (project rate)'
                    : r.client_override_for
                    ? ' (client rate)'
                    : ''}{' '}
                  — {formatMoney(r.amount)}/{r.rate_type === 'hourly' ? 'hr' : 'flat'}
                </option>
              ))}
            </select>
            {qFieldErrors['service_rate_id'] && (
              <span className="field-error">{qFieldErrors['service_rate_id']}</span>
            )}
          </label>

          {qFlat && (
            <label className="te-quick-field">
              <span>Flat fee for this entry *</span>
              <MoneyInput
                value={qFee}
                onChange={setQFee}
                disabled={createTime.isPending}
                aria-label="Flat fee for this entry"
              />
              {qFieldErrors['flat_amount'] && (
                <span className="field-error">{qFieldErrors['flat_amount']}</span>
              )}
            </label>
          )}

          {qSale && (
            <SoldAssetPicker
              className="te-quick-field"
              id="te-quick-asset"
              value={qAssetId}
              onChange={setQAssetId}
              disabled={createTime.isPending}
              error={qFieldErrors['asset_id']}
            />
          )}

          {qSale ? (
            <SaleDateField
              className="te-quick-field"
              id="te-quick-sale-date"
              value={qRange.start}
              onChange={(v) => setQRange({ ...qRange, start: v })}
              disabled={createTime.isPending}
              error={qFieldErrors['date']}
            />
          ) : (
            <TimeRangeFields
              value={qRange}
              onChange={setQRange}
              disabled={createTime.isPending}
              idPrefix="te-quick"
              flatFee={qFlat}
              errors={{
                start: qFieldErrors['date'],
                end: qFieldErrors['ended_at_epoch'],
                hours: qFieldErrors['hours'],
              }}
            />
          )}

          <label className="te-quick-field">
            <span>Description *</span>
            <textarea
              className="form-input te-quick-textarea"
              value={qDesc}
              onChange={(e) => setQDesc(e.target.value)}
              rows={3}
              maxLength={10000}
              placeholder="What did you work on?"
            />
            {qFieldErrors['description'] && (
              <span className="field-error">{qFieldErrors['description']}</span>
            )}
          </label>

          {qTopError && (
            <p className="te-error-banner" role="alert">
              {qTopError}
            </p>
          )}
        </div>
      </Modal>

      {/* Keyed by entry id: the modal seeds its fields from `entry` as initial
          state, so it must remount when a different row is opened. */}
      {editing && (
        <EditTimeEntryModal
          key={editing.id}
          open
          entry={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}
